/**
 * Loads, edits, and saves the `track_status` section of the per-facility
 * delivery-steps config. Mirrors the calling-config hook ergonomics: local
 * editable state, dirty tracking, and a partial Firestore write that touches
 * only `track_status` (sibling sections are never clobbered).
 */
import { toast } from "@risalabs_frontend_org/oasis-ui-kit";
import { useCallback, useEffect, useMemo, useState } from "react";
import { FirestoreService } from "../../../../../api/firebase/firestoreService";
import { FirestoreDocumentReference } from "../../../../../api/firebase/references";
import {
  DEFAULT_STALE_AFTER_MINUTES,
  DeliveryStepsDoc,
  TrackStatusConfig,
  TrackStatusSettings,
} from "../../../../../data-model/deliveryStepsConfig";
import { QueryGroup } from "../../../../../data-model/queryBuilder";
import { LocalStorageKeys } from "../../../../../enums/localStorageKeys";
import { getItemFromLocalStorage } from "../../../../../utils/localStorageHelper";
import {
  queryValueToRunWhen,
  runWhenToQueryValue,
} from "../shared/runWhenConversion";

const DEFAULT_ACTION = "fetch_and_run";
const DEFAULT_STATUS_FIELD = "meta.cmm_st_rpa_status";

interface TrackStatusForm {
  enabled: boolean;
  action: string;
  statusField: string;
  settings: TrackStatusSettings;
  query: QueryGroup;
}

const serialize = (form: TrackStatusForm): string => JSON.stringify(form);

function formFromConfig(
  config: TrackStatusConfig | undefined,
): TrackStatusForm {
  return {
    enabled: config?.enabled ?? false,
    action: config?.action ?? DEFAULT_ACTION,
    statusField: config?.status_field ?? DEFAULT_STATUS_FIELD,
    settings: { ...(config?.settings ?? {}) },
    query: runWhenToQueryValue(config?.run_when),
  };
}

/**
 * Hook driving the Track Status tab.
 *
 * @returns Editable form state, dirty/loading/saving flags, and save/discard handlers
 */
export function useTrackStatusConfig() {
  const [originalDoc, setOriginalDoc] = useState<DeliveryStepsDoc | null>(null);
  const [form, setForm] = useState<TrackStatusForm>(() =>
    formFromConfig(undefined),
  );
  const [savedSnapshot, setSavedSnapshot] = useState<string>("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const orgId =
    getItemFromLocalStorage(LocalStorageKeys.HEALTHCARE_FACILITY_ID) ?? "";

  const load = useCallback(async () => {
    if (!orgId.trim()) {
      setError("No healthcare facility id in local storage.");
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const path = FirestoreDocumentReference.deliveryStepsConfig(orgId);
      const doc =
        await FirestoreService.getDocumentOrNull<DeliveryStepsDoc>(path);
      setOriginalDoc(doc);
      const initial = formFromConfig(doc?.track_status);
      setForm(initial);
      setSavedSnapshot(serialize(initial));
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setLoading(false);
    }
  }, [orgId]);

  useEffect(() => {
    void load();
  }, [load]);

  const isDirty = useMemo(
    () => serialize(form) !== savedSnapshot,
    [form, savedSnapshot],
  );

  const setEnabled = useCallback(
    (enabled: boolean) => setForm((f) => ({ ...f, enabled })),
    [],
  );
  const setAction = useCallback(
    (action: string) => setForm((f) => ({ ...f, action })),
    [],
  );
  const setStatusField = useCallback(
    (statusField: string) => setForm((f) => ({ ...f, statusField })),
    [],
  );
  const setSetting = useCallback(
    (key: keyof TrackStatusSettings, value: number | undefined) =>
      setForm((f) => ({ ...f, settings: { ...f.settings, [key]: value } })),
    [],
  );
  const setQuery = useCallback(
    (query: QueryGroup) => setForm((f) => ({ ...f, query })),
    [],
  );

  const applySettingsForm = useCallback(
    (
      values: Pick<
        TrackStatusForm,
        "enabled" | "action" | "statusField" | "settings"
      >,
    ) => setForm((f) => ({ ...f, ...values })),
    [],
  );

  const handleDiscard = useCallback(() => {
    setForm(formFromConfig(originalDoc?.track_status));
  }, [originalDoc]);

  const handleSave = useCallback(async () => {
    if (!orgId.trim()) {
      toast.error("No healthcare facility id; cannot save.");
      return;
    }
    if (!originalDoc) {
      toast.error("No delivery-steps document to update.");
      return;
    }
    setSaving(true);
    try {
      const staleMinutes =
        originalDoc.global?.stale_threshold_minutes ??
        DEFAULT_STALE_AFTER_MINUTES;
      // Round-trip through JSON to drop `undefined` values (e.g. a cleared
      // numeric setting) — Firestore updateDoc rejects undefined field values.
      const trackStatus = JSON.parse(
        JSON.stringify({
          ...originalDoc.track_status,
          enabled: form.enabled,
          action: form.action,
          status_field: form.statusField,
          settings: form.settings,
          run_when: queryValueToRunWhen(form.query, staleMinutes),
        }),
      ) as TrackStatusConfig;
      await FirestoreService.updateDocument(
        `api_config/${orgId}/delivery-steps`,
        "v1",
        { track_status: trackStatus },
      );
      setOriginalDoc((prev) =>
        prev ? { ...prev, track_status: trackStatus } : prev,
      );
      setSavedSnapshot(serialize(form));
      toast.success("Track status configuration saved.");
    } catch {
      toast.error("Failed to save track status configuration.");
    } finally {
      setSaving(false);
    }
  }, [orgId, originalDoc, form]);

  return {
    form,
    loading,
    saving,
    error,
    isDirty,
    hasDoc: originalDoc != null,
    setEnabled,
    setAction,
    setStatusField,
    setSetting,
    setQuery,
    applySettingsForm,
    handleDiscard,
    handleSave,
  };
}
